import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, FileBadge, BadgeCheck, FolderOpen, FileText, Plus, Trash2, Loader2, ChevronDown, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import FileUploader from "@/components/FileUploader";
import { ExpiryBadge } from "@/components/ExpiryBadge";
import { confirmAction } from "@/components/ConfirmHost";
import { useAuth } from "@/context/AuthContext";
import { isOwner } from "@/lib/permissions";

const BUILTIN_ICONS = {
  insurance:    Shield,
  vat:          FileBadge,
  iso:          BadgeCheck,
  certificates: FileText,
  other:        FolderOpen,
};

const VARIANTS = {
  certifications: {
    kind: "certification",
    testid: "d4ps-documents-page",
    title: "D4PS certifications",
    intro: "A single home for D4PS's certifications. Create your own sections and upload certificates into each one.",
    addLabel: "Add a new certification",
    placeholder: "e.g. Operator licence, FORS Bronze, ISO 9001...",
    search: "Search certifications (e.g. FORS, ISO)...",
    empty: "No certifications yet — add your first one above (e.g. Operator licence, FORS, ISO 9001).",
    fields: [
      { key: "cert_number", label: "Certificate number" },
      { key: "expiry_date", label: "Expiry date", type: "date" },
    ],
    subtitle: (c) => (c.cert_number ? `No. ${c.cert_number}` : c.hint),
  },
  insurances: {
    kind: "insurance",
    testid: "d4ps-insurances-page",
    title: "D4PS insurances",
    intro: "All of D4PS's insurance policies in one place, with renewal warnings 30 days before they run out.",
    addLabel: "Add a new insurance",
    placeholder: "e.g. Goods in Transit, Fleet, Public Liability...",
    search: "Search insurances (e.g. GIT, fleet, insurer)...",
    empty: "No insurances yet — add your first one above (e.g. Goods in Transit, Fleet, Employers' Liability).",
    fields: [
      { key: "insurer", label: "Insurer" },
      { key: "cert_number", label: "Policy number" },
      { key: "cover_amount", label: "Cover amount" },
      { key: "start_date", label: "Start date", type: "date" },
      { key: "expiry_date", label: "Renewal date", type: "date" },
    ],
    subtitle: (c) => [c.insurer, c.cert_number && `Policy ${c.cert_number}`, c.cover_amount && `Cover ${c.cover_amount}`].filter(Boolean).join(" · "),
  },
};

const blankFields = (cfg) => Object.fromEntries(cfg.fields.map((f) => [f.key, ""]));

export default function D4PSDocuments({ variant = "certifications" }) {
  const { user } = useAuth();
  const cfg = VARIANTS[variant];
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [newFields, setNewFields] = useState(() => blankFields(cfg));
  const [saving, setSaving] = useState(false);
  const [openIds, setOpenIds] = useState(() => new Set());   // collapsed by default
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/d4ps-documents/categories", { params: { kind: cfg.kind } });
      setCats(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); setNewFields(blankFields(cfg)); setQuery(""); }, [variant]); // eslint-disable-line

  const toggle = (id) =>
    setOpenIds((curr) => {
      const next = new Set(curr);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const filteredCats = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return cats;
    return cats.filter((c) =>
      [c.name, c.hint, ...cfg.fields.map((f) => c[f.key])].some((v) => (v || "").toLowerCase().includes(q))
    );
  }, [cats, query, cfg]);

  // When searching, auto-open matching categories so the user immediately sees
  // what's inside without an extra click.
  useEffect(() => {
    if (!query.trim()) return;
    setOpenIds(new Set(filteredCats.map((c) => c.id)));
  }, [query, filteredCats]);

  const addCategory = async () => {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    try {
      await api.post("/d4ps-documents/categories", { name, kind: cfg.kind, ...newFields });
      setNewName(""); setNewFields(blankFields(cfg));
      toast.success("Added");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const removeCategory = async (cat) => {
    if (!(await confirmAction(`Remove "${cat.name}" and all its files? This can't be undone.`))) return;
    try {
      await api.delete(`/d4ps-documents/categories/${cat.id}`);
      toast.success("Removed");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid={cfg.testid} className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Company</div>
        <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-2">{cfg.title}</h1>
        <p className="text-sm text-slate-600 mt-1">{cfg.intro}</p>
      </div>

      <Card className="rounded-sm border border-slate-200 shadow-none p-5" data-testid="d4ps-add-section-card">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[240px] max-w-md">
            <Label className="text-xs text-slate-600">{cfg.addLabel}</Label>
            <Input
              data-testid="d4ps-new-section-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCategory(); } }}
              placeholder={cfg.placeholder}
              className="mt-1 rounded-sm border-slate-300"
            />
          </div>
          {cfg.fields.map((f) => (
            <div key={f.key} className={f.type === "date" ? "w-44" : "w-48"}>
              <Label className="text-xs text-slate-600">{f.label} (optional)</Label>
              <Input data-testid={`d4ps-new-${f.key}`} type={f.type || "text"} value={newFields[f.key] || ""}
                onChange={(e) => setNewFields((nf) => ({ ...nf, [f.key]: e.target.value }))} className="mt-1 rounded-sm border-slate-300" />
            </div>
          ))}
          <Button
            data-testid="d4ps-add-section-btn"
            onClick={addCategory}
            disabled={saving || !newName.trim()}
            className="rounded-sm bg-slate-900 hover:bg-slate-800"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
            Add
          </Button>
        </div>
      </Card>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <Input
          data-testid="d4ps-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={cfg.search}
          className="rounded-sm border-slate-300 pl-9"
        />
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : filteredCats.length === 0 ? (
        <div className="text-sm text-slate-500 border border-dashed border-slate-300 bg-slate-50 rounded-sm p-6 text-center" data-testid="d4ps-empty">
          {query ? <>Nothing matches &ldquo;{query}&rdquo;.</> : cfg.empty}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredCats.map((cat) => {
            const Icon = BUILTIN_ICONS[cat.id] || FolderOpen;
            const isOpen = openIds.has(cat.id);
            return (
              <Card
                key={cat.id}
                data-testid={`d4ps-doc-card-${cat.id}`}
                className="rounded-sm border border-slate-200 shadow-none overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => toggle(cat.id)}
                  data-testid={`d4ps-toggle-${cat.id}`}
                  className="w-full flex items-center justify-between gap-3 p-5 text-left hover:bg-slate-50 transition-colors"
                  aria-expanded={isOpen}
                >
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="h-9 w-9 inline-flex items-center justify-center rounded-sm bg-slate-900 text-white shrink-0">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 truncate">{cat.name}</div>
                      {cfg.subtitle(cat) && <div className="text-xs text-slate-500 mt-0.5 truncate">{cfg.subtitle(cat)}</div>}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    {cat.expiry_date && <ExpiryBadge date={cat.expiry_date} testid={`d4ps-expiry-${cat.id}`} />}
                    {isOwner(user) && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); removeCategory(cat); }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); removeCategory(cat); } }}
                        data-testid={`d4ps-delete-cat-${cat.id}`}
                        className="inline-flex items-center gap-1 rounded-sm text-xs text-red-600 hover:text-red-700 hover:bg-red-50 px-2 py-1"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Remove
                      </span>
                    )}
                    <ChevronDown
                      className={`w-5 h-5 text-slate-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    />
                  </div>
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 pt-1 border-t border-slate-100">
                    {!cat.builtin && <SectionDetails key={variant} cat={cat} cfg={cfg} onSaved={(c) => setCats((cs) => cs.map((x) => x.id === c.id ? c : x))} />}
                    <FileUploader entityType="d4ps" entityId={cat.id} label="Files" showExpiry showDocNumber />
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}


function SectionDetails({ cat, cfg, onSaved }) {
  const pick = (c) => Object.fromEntries(cfg.fields.map((f) => [f.key, c[f.key] || ""]));
  const [form, setForm] = useState({ name: cat.name, ...pick(cat) });
  const [saving, setSaving] = useState(false);
  const dirty = form.name !== cat.name || cfg.fields.some((f) => form[f.key] !== (cat[f.key] || ""));
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/d4ps-documents/categories/${cat.id}`, { ...form, kind: cfg.kind });
      onSaved(r.data);
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="flex flex-wrap items-end gap-3 py-4 mb-4 border-b border-slate-100" data-testid={`d4ps-details-${cat.id}`}>
      <div className="flex-1 min-w-[200px] max-w-sm">
        <Label className="text-xs text-slate-600">Name</Label>
        <Input data-testid={`d4ps-edit-name-${cat.id}`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm border-slate-300" />
      </div>
      {cfg.fields.map((f) => (
        <div key={f.key} className={f.type === "date" ? "w-44" : "w-48"}>
          <Label className="text-xs text-slate-600">{f.label}</Label>
          <Input data-testid={`d4ps-edit-${f.key}-${cat.id}`} type={f.type || "text"} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="mt-1 rounded-sm border-slate-300" />
        </div>
      ))}
      <Button data-testid={`d4ps-edit-save-${cat.id}`} onClick={save} disabled={!dirty || saving || !form.name.trim()} className="rounded-sm bg-slate-900 hover:bg-slate-800">
        {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Save
      </Button>
    </div>
  );
}
